import React, { useEffect, useMemo } from 'react'
import { useModal } from '../../../../components/useModal'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import TextInput from '../../../../components/FormElements/TextInput'
import { Button } from '@demandlocal/components'
import { get, omit, startCase } from 'lodash'
import GhostButton from '../../../../components/Buttons/GhostButton'
import { useExportToXLS } from '../../../../lib/hooks/useExportToXLS'
import { useInView } from 'react-intersection-observer'
import { motion } from 'framer-motion'
import CustomersUploadColumnsMapper from '../../../../components/CustomersUpload/ColumnsMapper'
import { useVerifyGoogleSpreadsheet } from '../../../../api/dealership/dsp/third_party/useVerifyGoogleSpreadsheet'
import { ReactComponent as LoadingIcon } from './../../../../images/icons/loading_icon.svg'
import LoadingComponent from '../../../../components/LoadingComponent'
import { customersMapperMongoColumns } from '../../../../lib/utils/customersMapperMongoColumns'
import { useGetGoogleServiceUser } from '../../../../api/dealership/customers/sources/useGetGoogleServiceUser'
import { useDealership } from '../../../../store/zustand/dealershipStore'

type Props = {
  databaseColumns?: Array<string>
  onSave: (values: FieldValues) => void
}

const GoogleSpreadsheetsFileMapper = (props: Props): JSX.Element => {
  const { databaseColumns, onSave } = props
  const { closeModal } = useModal()
  const { exportToCSV } = useExportToXLS()

  const dealership = useDealership()
  const {
    mutate,
    data: { data, name } = { data: [], name: '' },
    isLoading,
    isSuccess,
  } = useVerifyGoogleSpreadsheet()
  const { data: serviceUser, isLoading: isLoadingServiceUser } = useGetGoogleServiceUser()

  const [ref, inView, entry] = useInView({
    threshold: 1,
    triggerOnce: true,
    fallbackInView: true,
  })

  const formContext = useForm({
    mode: 'onSubmit',
  })
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors, isSubmitSuccessful },
  } = formContext

  useEffect(() => {
    if (!isLoadingServiceUser) setValue('service_email', serviceUser?.email)
  }, [isLoadingServiceUser, serviceUser?.email, setValue])

  const columns = useMemo(() => {
    const row = get(data, '[0]', [])
    return Object.keys(row).map((key) => {
      return {
        accessor: key,
        Header: key,
        columns: [{ accessor: key, Header: key }],
      }
    })
  }, [data])

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const rows = omit(values, 'spreadsheet_id')
    const rowsMapping = Object.entries(rows)
      .filter(([key, value]) => value)
      .map(([key, value]) => {
        return { [key]: value.value }
      })
    await onSave({
      rowsMapping,
      id: values.spreadsheet_id.match(/.+\/d\/([^/]+)/)[1],
      value: values.spreadsheet_id.match(/.+\/d\/([^/]+)/)[1],
      label: name,
    })
    closeModal()
  }
  return (
    <FormProvider {...formContext}>
      <form
        ref={ref}
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full'}>
        <div className={'flex flex-1 flex-col  overflow-hidden'}>
          <div className={'font-bold text-[28px] text-primary-xiketic'}>
            Import Google Sheet
          </div>
          <div className={'pt-8 pb-8 border-b-2 flex flex-col gap-8'}>
            <div className={'w-1/2'}>
              <TextInput
                disabled={true}
                inverted
                info={
                  'Go to your Google Spreadsheet and click on Share, then share your spreadsheet with this email'
                }
                name={'service_email'}
                label={'1. Share your Spreadsheet to your unique associated email.'}
              />
            </div>
            <div className={'w-1/2 flex items-center'}>
              <TextInput
                rules={{
                  required: 'This field is required',
                }}
                inverted
                label={'2. Paste your Spreadsheet URL here.'}
                placeholder={
                  'Ex. https://docs.google.com/spreadsheets/d/1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms/edit?resourcekey=0-TXxWtkIFpQHSPYCu0ueu2Q#gid=0'
                }
                name={'spreadsheet_id'}
                onChange={(value: string) => {
                  const spreadsheetId = value.match(/.+\/d\/([^/]+)/)?.[1]
                  if (spreadsheetId) {
                    mutate({
                      params: {
                        spreadsheet_id: spreadsheetId || value,
                        dealership_id: dealership?.id,
                      },
                    })
                  }

                  return spreadsheetId
                }}
                control={control}
              />
            </div>
          </div>
          {!isSuccess && data?.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={'flex flex-1 items-center justify-center flex-col gap-4'}>
              {isLoading ? (
                <LoadingComponent title={'Loading data...'} />
              ) : (
                <div className={'flex flex-col items-center gap-4'}>
                  <LoadingIcon />
                  <span className={'font-bold text-xs text-blue-primary'}>
                    Your data will appear here
                  </span>
                </div>
              )}
            </motion.div>
          )}
          {isSuccess && (
            <div className={'flex flex-1 flex-col pt-8 relative'}>
              <div className={'flex flex-row justify-between items-center pb-3'}>
                <div className={'flex flex-col'}>
                  <span className={'font-bold text-base'}>Match rows</span>
                  <span className={'mt-2 font-medium text-sm'}>
                    For a precise upload, please match the table headers below
                    {data?.length > 200 && (
                      <span className={'ml-2 font-normal text-xs'}>
                        (Showing first 200 rows)
                      </span>
                    )}
                  </span>
                </div>
                <div>
                  <GhostButton
                    transparent={true}
                    onClick={(e) => {
                      e.preventDefault()
                      exportToCSV(
                        [(databaseColumns ?? [])?.map((column) => startCase(column))],
                        'dl_saas_template.csv',
                        true,
                        'csv',
                      )
                    }}>
                    Download CSV Template
                  </GhostButton>
                </div>
              </div>
              {errors.submitError && (
                <div
                  className={'pb-3 font-medium text-sm text-secondary-bittersweet-red'}>
                  {/** @ts-ignore **/}
                  {errors.submitError.message}
                </div>
              )}
              <div
                className={
                  'flex flex-1 flex-col max-h-[calc(100vh_-_450px)] overflow-auto flex-1'
                }>
                <CustomersUploadColumnsMapper
                  data={data}
                  columns={columns}
                  rowsOptions={customersMapperMongoColumns}
                />
                {!inView && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={'flex flex-1 items-center justify-center'}>
                    Loading...
                  </motion.div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className={'h-20 items-center flex justify-between border-t-2 py-4'}>
          <Button
            variant={'secondary'}
            disabled={isSubmitting && isSubmitSuccessful}
            onClick={(e) => {
              e.preventDefault()
              closeModal()
            }}>
            Close
          </Button>
          <Button disabled={isSubmitting && isSubmitSuccessful} type={'submit'}>
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default GoogleSpreadsheetsFileMapper
